// =============================================================================
//  Copyright : 2022 semantics GmbH
//  Date      : $Date: 2025-05-05 14:46:04 +0200 (Mon, 05 May 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: o.powroznik $
//  Revision  : $Rev: 152229 $
//  Purpose   : presentation wiki templates
// =============================================================================
// ---Third-party imports
import React, {useRef} from 'react'
import {createContext} from 'react'
import {Style} from 'react-head'
import clsx from 'clsx'
import {Message} from 'semantic-ui-react'

// ---Framework imports
import {Sel, Actions} from 'uix/registries'
import log from 'uix/logging'
import {windowDevicePixelRatio} from 'uix/utils'
import {isDigit, strip, sum, range, keys, isNil, groupBy, sortBy} from 'ldash'
import {TagMatcher, BOOL_STR, useWikiContext, schemaifyAttributes} from 'uix/wiki/utils'
import {
  useEffect,
  useSelector,
  useCallback,
  useState,
  useInView,
  useDimensions,
  useDebounce,
  useMemo,
  useApi,
} from 'uix/hooks'
import {VIcon} from 'graphics'
import {TagCloud} from 'uix/components/tagcloud'
import {RawLink} from 'uix/components/links'

// ---Local imports
import S from './templates.scss'
import GlobalS from './index.scss'
import {
  TextTicker,
  Button,
  AudioTicker,
  Showcase,
  AiPrompt,
  AiImage,
  PageViewer,
  Modal,
  MediaPlayer,
  AccessWarning,
} from './components'
import {useCaptionLink, usePageIdMdCaption} from './hooks'

// ---Exports

const PNG_CACHE = {}
const createPNGImageDataURI = ({width, height, backgroundColor = 'white', text, cacheKey}) => {
  if (cacheKey && PNG_CACHE[cacheKey]) {
    return PNG_CACHE[cacheKey]
  }
  // Create a canvas element with the specified width and height
  let canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  // Get the canvas 2D context
  let ctx = canvas.getContext('2d')

  // Draw a background color if needed (replace 'white' with any color)
  ctx.fillStyle = backgroundColor
  ctx.fillRect(0, 0, width, height)
  if (text) {
    // Draw text on the canvas
    ctx.font = '10px sans-serif'
    ctx.fillStyle = 'black'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(text, width / 2, height / 2)
  }

  // Convert the canvas content to a PNG data URI
  let res = canvas.toDataURL('image/png')
  if (cacheKey) {
    PNG_CACHE[cacheKey] = res
  }
  return res
}

const useWebcacheWidth = renderedWidth => {
  let wcSizes = useSelector(root => root.config.instance.webcache.sizes)
  renderedWidth = renderedWidth * windowDevicePixelRatio
  // wc-width 0 (org-width) will be ignored
  let bestFitWidth = wcSizes.filter(w => w <= renderedWidth && w <= 2000).at(-1)
  bestFitWidth = bestFitWidth || 504

  // never decrease
  let [maxWidth, setMaxWidth] = useState(0)
  useEffect(() => {
    if (bestFitWidth > maxWidth) setMaxWidth(bestFitWidth)
  }, [bestFitWidth])

  return Math.max(bestFitWidth, maxWidth)
}

const useImageInfo = ({
  id,
  imgWidth,
  imgHeight,
  boxId,
  boxWidth,
  boxHeight,
  requestedWidth,
  renderedWidth,
}) => {
  let apiurl = useSelector(Sel.apiurl)
  let [isError, setIsError] = useState(false)
  let wikictx = useWikiContext()
  let {assetsById = {}} = wikictx
  // XXX: this "choose requested resolution by tracking dimensions"
  // does not really work: it's hard to prevent that resolution changing triggers a dimension change
  let wcWidth = useWebcacheWidth(renderedWidth)
  if (!requestedWidth) {
    // if no external width given - pin down resolution (see XXX above)
    wcWidth = 1000
  }
  let imgsrc = null
  let aspectRatio = 1
  // XXX: remove dependency on wikiContext
  let assetInfo = assetsById[id]
  if (assetInfo && assetInfo.datatype == 3) {
    if (assetInfo.page.crop) {
      // cropped image
      let {width, height} = assetInfo.page.crop
      aspectRatio = height / width
    } else {
      // full image
      let {width, height} = assetInfo.page
      aspectRatio = height / width
    }
  }
  // the prefered data transport channel
  if (boxId && boxWidth && boxHeight) {
    // cropped image
    aspectRatio = boxHeight / boxWidth
  } else if ((imgWidth, imgHeight)) {
    // full image
    aspectRatio = imgHeight / imgWidth
  }

  let createErrorImage = text =>
    createPNGImageDataURI({
      text,
      width: wcWidth,
      height: Math.floor(wcWidth * aspectRatio),
      backgroundColor: '#FFAAAACC',
    })

  if (renderedWidth === -1 && !!requestedWidth) {
    // useDimensions needs another rendering pass
    // so use a placeholder first
    imgsrc = createPNGImageDataURI({
      width: 100,
      height: Math.floor(100 * aspectRatio),
      backgroundColor: '#00000000',
      cacheKey: aspectRatio,
    })
  } else if (isError) {
    imgsrc = createErrorImage(`error: ${id}`.slice(0, 70))
  } else if (id) {
    imgsrc = `/download/webcache/${wcWidth}/${id}`
    if (assetInfo) {
      let otid = assetInfo.otlink || id // either linked or physical page
      if (assetInfo.datatype == 3) {
        if (assetInfo.page?.crop) {
          let {opbid} = assetInfo.page.crop
          imgsrc = `/image/pagebox/${opbid}/${wcWidth}`
        } else {
          imgsrc = `/download/webcache/${wcWidth}/${otid}`
        }
      } else if (assetInfo.datatype == 9 && assetInfo.subtype == 2) {
        // webimage
        imgsrc = `/domainimage/${otid}`
      }
    }
    imgsrc = apiurl.href(imgsrc)
  } else {
    imgsrc = createErrorImage('no id')
  }

  let onError = useCallback(() => setIsError(true), [])
  return {imgsrc, aspectRatio, onError, awstate: assetInfo?.awstate}
}

const Image = ({
  id,
  width,
  alt,
  style,
  onClick,
  imgWidth,
  imgHeight,
  imageLink,
  boxId,
  boxWidth,
  boxHeight,
  focusPoints = [],
  applyTemplates,
}) => {
  let [dimensions, ref] = useDimensions({defaults: {width: -1, height: -1}})
  let [renderedWidth] = useDebounce(dimensions.width, 500, {leading: true})
  let {imgsrc, aspectRatio, onError, awstate} = useImageInfo({
    id,
    imgWidth,
    imgHeight,
    boxId,
    boxWidth,
    boxHeight,
    requestedWidth: width,
    renderedWidth,
  })
  //FocusPoint related
  let [zoomState, setZoomState] = useState({active: false, transform: ''})
  let [currentFocus, setCurrentFocus] = useState(undefined)

  let resetZoom = () => {
    if (zoomState.active) {
      setZoomState({active: false, transform: ''})
    }
  }

  const renderImg = () => (
    <img
      ref={ref}
      style={style}
      src={imgsrc}
      alt={alt}
      onClick={onClick}
      onError={onError}
      loading="lazy"
    />
  )

  const wrapImg = content =>
    imageLink ? (
      <a style={{cursor: 'pointer'}} href={imageLink}>
        {content}
      </a>
    ) : (
      content
    )

  let zoomFocusIndices = focusPoints
    .map((fp, index) => (fp.attrib?.zoomfocus === 'true' ? index : null))
    .filter(index => index !== null)

  let totalFocus = zoomFocusIndices.length

  let nextFocus = () => {
    setCurrentFocus(prev => {
      let currentIdx = zoomFocusIndices.indexOf(prev)
      let nextIdx = (currentIdx + 1) % totalFocus
      return zoomFocusIndices[nextIdx]
    })
  }

  let prevFocus = () => {
    setCurrentFocus(prev => {
      let currentIdx = zoomFocusIndices.indexOf(prev)
      let prevIdx = (currentIdx - 1 + totalFocus) % totalFocus
      return zoomFocusIndices[prevIdx]
    })
  }
  return focusPoints.length > 0 ? (
    <>
      {totalFocus > 1 && (
        <div className={clsx(S.carouselNav, zoomState.active && S.show)}>
          <a onClick={currentFocus > 0 ? prevFocus : undefined}>
            <VIcon className={clsx(S.prev, currentFocus <= 0 && S.disabled)} name="arrow left" />
          </a>
          <span>
            {zoomFocusIndices.indexOf(currentFocus) + 1} / {totalFocus}
          </span>
          <a onClick={currentFocus < totalFocus - 1 ? nextFocus : undefined}>
            <VIcon
              className={clsx(S.prev, currentFocus >= totalFocus - 1 && S.disabled)}
              name="arrow right"
            />
          </a>
        </div>
      )}
      <div ref={ref} className={S.focusGraphicCnt} onClick={resetZoom}>
        <div style={{overflo: 'hidden'}}>
          {wrapImg(
            <AccessWarning id={id} awstate={awstate}>
              <img
                style={{
                  ...style,
                  transform: zoomState.active ? zoomState.transform : 'none',
                  cursor: imageLink ? 'pointer' : zoomState.active ? 'pointer' : 'default',
                }}
                src={imgsrc}
                alt={alt}
                onClick={onClick}
                onError={onError}
                loading="lazy"
              />
            </AccessWarning>
          )}
        </div>
        <FocusPoints
          focusPoints={focusPoints}
          dimensions={dimensions}
          aspectRatio={aspectRatio}
          applyTemplates={applyTemplates}
          setZoomState={setZoomState}
          zoomState={zoomState}
          setCurrentFocus={setCurrentFocus}
          currentFocus={currentFocus}
        />
      </div>
    </>
  ) : (
    wrapImg(
      <AccessWarning id={id} awstate={awstate} onClick={onClick}>
        {renderImg()}
      </AccessWarning>
    )
  )
}

export const GraphicMatcher = TagMatcher(
  'Graphic',
  ({parentEl, childEls, applyTemplates, ...attrs}) => {
    let {
      // source
      external,
      hideLink,
      id,
      imageLink,
      link,
      linkid,
      url,
      // resolved image source
      imgWidth,
      imgHeight,
      // resolved pagebox crop source
      boxId,
      boxWidth,
      boxHeight,
      // content
      alt,
      title,
      caption,
      showCaption,
      // styling
      style,
      height,
      position,
      width,
      marginTop,
      marginRight,
      marginBottom,
      marginLeft,
      float,
      // features
      interact,
      framed,
      frameColor,
    } = attrs

    let {mobile} = useWikiContext()
    let apiurl = useSelector(Sel.apiurl)

    let gGroupParent = parentEl.tag === 'GraphicGroup'
    let focusPoints = childEls?.filter(e => e.tag == 'FocusPoint')
    let gridParent = gGroupParent && parentEl.attrib.grid
    let gap = null
    let modalTypeIcon = null

    if (gridParent) {
      parentEl.attrib.gap ? (gap = parentEl.attrib.gap) : null
    }
    let imgStyle = {objectPosition: position || 'top'}
    if (gGroupParent) {
      parentEl.attrib.interact ? (interact = parentEl.attrib.interact) : null
      parentEl.attrib.height ? (height = parentEl.attrib.height) : null
      parentEl.attrib.framed ? (framed = parentEl.attrib.framed) : null
      parentEl.attrib.frameColor ? (frameColor = parentEl.attrib.frameColor) : null
      imgStyle = {...imgStyle, '--widget_graphic_img_width': '100%'}
    }

    let modalEl = childEls.filter(e => e.tag == 'Modal').at(0)
    let isModalMode = interact == 'zoomModal' || !isNil(modalEl)
    let onGraphicClick = isModalMode ? () => setModalOpen(true) : undefined

    if (isModalMode) {
      modalTypeIcon =
        {zoom: 'zoom', viewer: 'book', text: 'info'}[(modalEl?.attrib?.type)] || 'zoom'
    }

    if (!alt) {
      alt = title
    }
    let imgSourceProps = {imgWidth, imgHeight, boxId, boxWidth, boxHeight}
    let img = (
      <Image
        id={id}
        width={width}
        style={imgStyle}
        alt={alt}
        imageLink={imageLink}
        onClick={onGraphicClick}
        focusPoints={focusPoints}
        applyTemplates={applyTemplates}
        {...imgSourceProps}
      />
    )
    if (link) {
      img = (
        <RawLink
          onClick={onGraphicClick}
          href={apiurl.href(link)}
          target={external ? '_blank' : undefined}
        >
          {img}
        </RawLink>
      )
    }

    let captionLink = (
      <div className={clsx(S.description, showCaption && S.showCaption)}>
        {useCaptionLink(caption, linkid, url, id, !gGroupParent && true)}
      </div>
    )
    let modalCaption = (
      <div
        className={clsx(S.description, S.inModal, mobile && S.mobile, showCaption && S.showCaption)}
      >
        {useCaptionLink(caption, linkid, url, id, true)}
      </div>
    )

    let graphicStyle = {
      marginBottom,
      marginLeft: !mobile && marginLeft,
      marginRight: !mobile && marginRight,
      marginTop,
      height,
      float,
      padding: `${gap}px`,
      borderColor: frameColor,
    }

    let cssImgWidth = width

    if (mobile) {
      cssImgWidth = '100%'
    } else {
      if (gGroupParent) {
        cssImgWidth = null
      }
    }

    let res = (
      <div
        style={{
          ...graphicStyle,
          ...style,
          float: mobile ? 'none' : float,
          width: cssImgWidth,
        }}
        className={clsx(
          S.graphic,
          focusPoints.length > 1 && S.focusParent,
          framed && S.framed,
          isModalMode && S.modalMode,
          mobile && S.mobile,
          'presGraphic'
        )}
        title={title}
      >
        {img}
        {isModalMode && (
          <>
            <div className={clsx(S.clickable, gGroupParent && S.inParent)}></div>
            <Button
              size="large"
              style={{
                color: 'var(--presentation_themeColor)',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              }}
              onClick={onGraphicClick}
              className={S.zoomButton}
              icon={modalTypeIcon}
              circle
              inverted
            />
          </>
        )}
        {!hideLink && captionLink}
      </div>
    )

    let [modalOpen, setModalOpen] = useState(false)

    if (isModalMode) {
      let modalContent = null
      let bgid = null
      let scrolling = false
      if (interact == 'zoomModal') {
        modalContent = (
          <PageViewer
            mobile={mobile}
            optionSet="grid"
            otids={[id]}
            height="100%"
            zoomControls="inOut"
          />
        )
      } else if (!isNil(modalEl)) {
        modalContent = applyTemplates(modalEl.children.find(e => e.tag == 'ModalContent'))
        bgid = modalEl.attrib.bgid
        scrolling = true
        if (modalEl.attrib.hideCaption) {
          modalCaption = null
        }
      }
      if (modalOpen) {
        //XXX: rendering image before modal prevent image unmounting on modal rendering
        res = (
          <>
            {res}
            <Modal
              mobile={mobile}
              scrolling={scrolling}
              open={modalOpen}
              onClose={() => setModalOpen(false)}
              imageId={bgid}
              caption={modalCaption}
            >
              {modalContent}
            </Modal>
          </>
        )
      }
    }

    return res
  },
  'Render a Graphic'
)

const FocusPoints = ({
  focusPoints,
  dimensions,
  aspectRatio,
  applyTemplates,
  setZoomState,
  zoomState,
  currentFocus,
  setCurrentFocus,
}) => {
  const zoomToFocusPoint = (fP, dimensions, aspectRatio, setZoomState) => {
    if (!fP || fP.attrib.zoomfocus !== 'true') return

    let size = parseFloat(fP.attrib.size?.replace('%', ''))
    let zoom = parseFloat((fP.attrib.zoomlevel || '100%').replace('%', '')) / 100
    let focusWidthPx = (dimensions.width * size) / 100
    let focusHeightPx = focusWidthPx * aspectRatio
    let offsetX = (parseFloat(fP.attrib.left) / 100) * dimensions.width
    let offsetY = (parseFloat(fP.attrib.top) / 100) * dimensions.height
    let scale = Math.max(dimensions.width / focusWidthPx, dimensions.height / focusHeightPx) * zoom
    let translateX = -(offsetX - dimensions.width / 2)
    let translateY = -(offsetY - dimensions.height / 2)

    setZoomState({
      active: true,
      transform: `scale(${scale}) translate(${translateX}px, ${translateY}px)`,
    })
  }

  useEffect(() => {
    if (currentFocus === null || currentFocus === undefined) return
    zoomToFocusPoint(focusPoints[currentFocus], dimensions, aspectRatio, setZoomState)
  }, [currentFocus])

  const handleFocusClick = (fP, id) => {
    setCurrentFocus(id)
    zoomToFocusPoint(fP, dimensions, aspectRatio, setZoomState)
  }

  let hasChildTag = (children, tagName, maxDepth, depth = 0) => {
    if (depth > maxDepth) return false
    return children.some(
      child =>
        child.tag === tagName ||
        (child.children && hasChildTag(child.children, tagName, maxDepth, depth + 1))
    )
  }

  let getChildrenByTag = (children, tagName, maxDepth, depth = 0, found = []) => {
    if (depth > maxDepth) return found

    for (let child of children) {
      if (child.tag === tagName) {
        found.push(child)
      }
      if (child.children) {
        getChildrenByTag(child.children, tagName, maxDepth, depth + 1, found)
      }
    }

    return found
  }

  const removeFPContent = nodes => {
    if (!Array.isArray(nodes)) return []

    return nodes
      .filter(node => node.tag !== 'FocusPointContent')
      .map(node => ({
        ...node,
        children: removeFPContent(node.children || []),
      }))
  }

  return (
    <>
      {focusPoints.map((fP, i) => {
        let size = parseFloat(fP.attrib.size?.replace('%', ''))
        let height = 'auto'
        let width = 'max-content'
        let widthPx = (dimensions.width * size) / 100

        let hasGraphicChild = hasChildTag(fP.children, 'Graphic', 3)

        if (fP.attrib.zoomfocus === 'true') {
          width = fP.attrib.size
          // height = `${widthPx * aspectRatio}px`
        }
        if (hasGraphicChild) {
          width = fP.attrib.size
          // height = 'auto'
        }
        let focusContent = getChildrenByTag(fP.children, 'FocusPointContent', 2)

        if (focusContent[0] && !focusContent[0].attrib?.left) {
          focusContent[0].attrib.left = fP.attrib.left
          focusContent[0].attrib.top = fP.attrib.top
        }

        let filteredFP = removeFPContent(fP.children)

        return (
          <div key={i}>
            <div
              className={clsx(
                S.focusPointWrap,
                fP.attrib.zoomfocus === 'true' && S.zoomable,
                hasGraphicChild && S.hasGraphic
              )}
              onClick={() => handleFocusClick(fP, i)}
              style={{
                top: `${fP.attrib.top}`,
                left: `${fP.attrib.left}`,
                width,
                ...(hasGraphicChild && fP.attrib.pulseColor
                  ? {'--widget_fP_bgColor': fP.attrib.pulseColor}
                  : {}),
                fontSize: fP.attrib.fontsize,
                height,
                opacity: zoomState.active ? 0 : 1,
                pointerEvents: zoomState.active && 'none',
                transition: `opacity ${zoomState.active ? '600ms ease-out' : '300ms ease-in 1.1s'}`,
              }}
            >
              {applyTemplates(filteredFP)}
            </div>
            {zoomState.active && currentFocus === i && applyTemplates(focusContent)}
          </div>
        )
      })}
    </>
  )
}

export const FocusPointContentMatcher = TagMatcher(
  // Child of FocusPoint
  // because of dependency, most code is in:
  // Graphic -> Image -> FocusPoints
  'FocusPointContent',
  ({applyTemplates, top, left, width, color, borderRadius, backgroundColor, padding, ...props}) => {
    const [visible, setVisible] = useState(false)
    useEffect(() => {
      const timer = setTimeout(() => setVisible(true), 700) //delay s.CSS grpahicGroup -> img transition
      return () => clearTimeout(timer)
    }, [])

    return (
      <div
        style={{
          position: 'absolute',
          '--widget_fPContent_top': top,
          '--widget_fPContent_color': color,
          '--widget_fPContent_left': left,
          '--widget_fPContent_width': width,
          '--widget_fPContent_padding': padding,
          '--widget_fPContent_borderRadius': borderRadius,
          '--widget_fPContent_bgColor': backgroundColor,
          opacity: visible ? 1 : 0,
          transition: 'opacity 300ms ease-in',
        }}
        className={S.focusPointContent}
      >
        {applyTemplates()}
      </div>
    )
  },
  {},
  'Content that gets revealed after FocusPoint Action'
)

export const FocusPointMatcher = TagMatcher(
  // because of dependency, most code is in:
  // Graphic -> Image -> FocusPoints
  'FocusPoint',
  ({applyTemplates}) => {
    return <div className={S.focusPoint}>{applyTemplates()}</div>
  },
  {},
  'Relative Element in Graphic'
)

const Carousel = ({children, applyTemplates, className}) => {
  let [currentIndex, setCurrentIndex] = useState(0)
  let totalChildren = children.length
  let {mobile} = useWikiContext()

  let getActualIndex = index => (index + totalChildren) % totalChildren

  let goNext = () => {
    setCurrentIndex(prevIndex => getActualIndex(prevIndex + 1))
  }

  let goPrev = () => {
    setCurrentIndex(prevIndex => getActualIndex(prevIndex - 1))
  }
  let transformValue = `translateX(-${currentIndex * 100}%)`

  let indicators = (
    <div className={S.carouselIndicators}>
      {[...Array(totalChildren)].map((_, index) => (
        <div
          key={index}
          className={clsx(S.indicator, {[S.active]: index === currentIndex})}
          onClick={() => setCurrentIndex(index)}
        />
      ))}
    </div>
  )

  let navButtonStyle = {
    color: 'white',
    border: '1px solid transparent',
  }
  let navButtons = (
    <>
      <Button
        className={clsx(S.carouselButton, S.prev)}
        onClick={goPrev}
        size="tiny"
        inverted
        style={navButtonStyle}
        icon="chevron left"
      />
      <Button
        className={clsx(S.carouselButton, S.next)}
        onClick={goNext}
        size="tiny"
        inverted
        style={navButtonStyle}
        icon="chevron right"
      />
    </>
  )
  return (
    <div className={clsx(S.carouselContainer, className, mobile && S.mobile)}>
      {indicators}
      {navButtons}
      <div className={S.carousel} style={{transform: transformValue}}>
        {children.map((_, index) => (
          <div key={index} className={clsx(S.carouselItem, index === currentIndex && S.active)}>
            {applyTemplates(children[getActualIndex(index)])}
          </div>
        ))}
      </div>
    </div>
  )
}

export const GraphicGroupMatcher = TagMatcher(
  'GraphicGroup',
  ({applyTemplates, type, caption, linkid, url, childEls, style, align}) => {
    let {mobile} = useWikiContext()
    let margin = mobile ? '1em 0' : null

    if (type === 'carousel') {
      return (
        <Carousel
          className={clsx(S.graphicGroupContainer, 'presGraphicGroup')}
          children={childEls}
          applyTemplates={applyTemplates}
        />
      )
    } else {
      return (
        <div className={clsx(S.graphicGroupContainer, 'presGraphicGroup', mobile && S.mobile)}>
          <div
            style={{
              '--widget_graphicGroup_margin': margin,
              '--widget_graphicGroup_align': align,
              ...style,
            }}
            className={S.graphicGroup}
          >
            {applyTemplates(childEls)}
          </div>
          {caption && (
            <div className={clsx(S.caption, mobile && S.mobile)}>
              {useCaptionLink(caption, linkid, url, null, true)}
            </div>
          )}
        </div>
      )
    }
  },
  {
    align: 'center',
    type: [null, 'carousel'],
    style: '',
    caption: '',
    linkid: '',
    url: '',
    // used by Graphic (parentEl.attrib):
    frameColor: 'white',
    framed: false,
    gap: '6',
    height: '240px',
    interact: ['zoomModal'],
  },
  'Renders a Group of Graphics'
)

export const GridElement = TagMatcher(
  'GridElement',
  ({applyTemplates, position}) => {
    return <div className={S[position]}>{applyTemplates()}</div>
  },
  {
    position: [
      'top-left',
      'top-center',
      'top-right',
      'center-left',
      'center-center',
      'center-right',
      'bottom-left',
      'bottom-center',
      'bottom-right',
    ],
  }
)

export const Quote = TagMatcher(
  'Quote',
  ({applyTemplates, url, caption, linkid, margin, padding, pageid, type}) => {
    let [ref, inView] = useInView({triggerOnce: true})
    let {mobile} = useWikiContext()
    let md_caption = usePageIdMdCaption(inView && !caption ? pageid : null)
    if (pageid && !caption) {
      caption = md_caption
    }
    let quoteCaption = useCaptionLink(caption, linkid, url, null, true)
    let cntPadding = mobile && '1em 0'
    return (
      <div
        style={{margin, padding: cntPadding}}
        className={clsx(S.quoteContainer, 'presQuoteCnt', type == 'minimal' && S.minimal)}
        ref={ref}
      >
        <div style={{padding}} className={clsx(S.quote, 'presQuote')}>
          <span className={S.symbol}>“</span>
          {applyTemplates()}
        </div>
        <div className={S.caption}>{quoteCaption}</div>
      </div>
    )
  },
  {type: ['default', 'minimal'], linkid: '', caption: '', url: '', padding: '2em', margin: '1em'}
)

export const More = TagMatcher(
  'More',
  ({applyTemplates, text}) => {
    let [show, setShow] = useState(false)
    let {mobile} = useWikiContext()
    let toggleMore = () => {
      setShow(!show)
    }
    let [dimensions, refDimensions] = useDimensions({defaults: null})
    dimensions = useMemo(() => dimensions, [dimensions.width])
    let moreStyle =
      dimensions.height > 0
        ? {
            position: 'relative',
            opacity: '1',
            '--more_end_height': `calc(${dimensions.height}px + var(--presentation_fontSize) + 1em`,
            '--more_start_height': '0px',
          }
        : null
    return (
      <div
        className={clsx(
          S.moreContainer,
          text && S.textShown,
          show && S.show,
          'presMore',
          mobile && S.mobile
        )}
      >
        {!text && (
          <Button
            className={S.moreButton}
            onClick={toggleMore}
            size="tiny"
            inverted
            style={{
              color: 'var(--presentation_themeColor)',
              border: '1px solid var(--presentation_themeColor)',
            }}
            icon={show ? 'minus' : 'plus'}
          />
        )}
        <div className={S.indicator}>
          {text && (
            <span onClick={toggleMore} className={S.text}>
              {text}
            </span>
          )}
          <span onClick={toggleMore} className={S.line}></span>
        </div>
        <span className={S.lineBottom}></span>
        <div style={moreStyle} className={S.more}>
          <div ref={refDimensions}>{applyTemplates()}</div>
        </div>
      </div>
    )
  },
  {}
)

export const Column = TagMatcher(
  'Column',
  ({applyTemplates, childEls}) => {
    return <>{applyTemplates(childEls)}</>
  },
  'Column for Columns'
)

export const Columns = TagMatcher(
  'Columns',
  ({applyTemplates, width, gap, childEls, style}) => {
    let {mobile} = useWikiContext()

    // distribute even columns or use width
    let widthArray = width
      ? String(width)
          .split(',')
          .filter(s => isDigit(strip(s)))
          .map(s => parseInt(s))
      : Array(childEls.length).fill(Math.floor(100 / childEls.length))

    // fill up missing widths (distribute remaining percentage evenly)
    let unset = 100 - sum(widthArray)
    let delta = childEls.length - widthArray.length
    if (delta > 0) {
      widthArray = [...widthArray, ...range(delta).map(i => Math.floor(unset / delta))]
    }

    let children = childEls.map((childEl, i) => {
      let width = widthArray[i]
      let clmnStyle = {
        flex: `1 0 calc(${width}% - ${gap}px)`,
      }
      if (mobile) {
        clmnStyle = {flex: '100%'}
      }
      return (
        <div key={i} className={S.column} style={clmnStyle}>
          {applyTemplates(childEl)}
        </div>
      )
    })
    let newStyle = {...style, gap: `${gap}px`}
    return (
      <div style={newStyle} className={clsx(S.columns, mobile && S.mobile)}>
        {children}
      </div>
    )
  },
  'Creates Column Layout, takes direct children as column elements'
)

const TextTickerControl = ({wikipaneId, audio, audioDelay, ...props}) => {
  /* control visibility/loading and combines a TextTicker with a AudioTicker
   */
  let {mobile} = useWikiContext()

  let [ref, inView] = useInView({triggerOnce: false})
  let [playerState, setPlayerState] = useState({muted: false, playing: false, ended: false})
  let {playing} = playerState

  let setPlayer = props => {
    return setPlayerState({...playerState, ...props})
  }

  useEffect(() => {
    if (!audio) {
      setPlayer({playing: true})
    }
    if (!inView) {
      setPlayer({playing: false})
    }
  }, [inView])

  let playHandler = () => {
    setPlayer({playing: true})
  }
  return (
    <>
      <div className={S.tickerControl}>
        {audio && (
          <AudioTicker
            inView={inView}
            audioDelay={audioDelay}
            started={inView && playing}
            audio={inView && audio}
            playerState={playerState}
            setPlayerState={setPlayer}
          />
        )}
      </div>
      <div style={{position: 'relative', clear: 'both'}} ref={ref}>
        {!playing && inView && (
          <div className={S.overlay}>
            <Button
              style={{
                color: 'var(--presentation_themeColor)',
                border: '1px solid var(--presentation_themeColor)',
              }}
              size="huge"
              inverted
              icon="play"
              circle
              onClick={playHandler}
            />
          </div>
        )}
        <TextTicker
          playing={playing}
          audio={audio}
          mobile={mobile}
          started={inView && playing}
          viewerId={wikipaneId}
          {...props}
        />
      </div>
    </>
  )
}

const DOTS = String.fromCodePoint(0x2026)
const defaultsTicker = {
  id: null,
  startIndex: 0,
  words: null, // relative to startIndex
  stopIndex: null, // not needed if words given
  pauses: null,
  sep: ` ${DOTS} `,
}

// only for auto complete setup, is manually handled in TextTickerMatcher
export const TickerMatcher = TagMatcher('TickerPage', () => null)

// XXX: child relation is not handled since schema derived completion
//~ completion: () => ({
//~ '!top': ['TextTicker'],
//~ TextTicker: {
//~ attrs: decodeCompletionAttrs(defaultsTextTicker),
//~ children: ['TickerPage', 'Description'],
//~ }),
export const TextTickerMatcher = {
  tagMatcher: 'TextTicker',
  match: ({tag}) => tag == 'TextTicker',
  build: ({attrib, children, applyTemplates, wikipaneId, templates}) => {
    let descrEl = children.find(e => e.tag == 'Description')
    let renderedDescr = descrEl ? [descrEl.text, applyTemplates(descrEl.children)] : null

    let template = templates.TickerMatcher
    let sequences = children
      .filter(e => e.tag == 'TickerPage' && isDigit(e.attrib.id))
      .map(e => ({otid: e.attrib.id, ...schemaifyAttributes(e.attrib, template.schema)}))

    return (
      <TextTickerControl
        description={renderedDescr}
        sequences={sequences}
        {...attrib}
        wikipaneId={wikipaneId}
      />
    )
  },
}

const MODELS = ['text-davinci-003', 'text-curie-001', 'text-babbage-001', 'text-ada-001']
export const AiPromptMatcher = TagMatcher(
  'AiPrompt',
  ({
    text,
    debug,
    model = 'text-davinci-003',
    interactive = false,
    seed = 0,
    header,
    context,
    suffix,
  }) => {
    let props = {text, debug, model, interactive, seed, header, context, suffix}
    return <AiPrompt {...props} />
  },
  {model: MODELS, interactive: 'true', header: ['prompt', '...']},
  'OpenAi Prompt'
)

export const AiImageMatcher = TagMatcher(
  'AiImage',
  AiImage,
  {seed: '', width: '', height: ''},
  'OpenAi Image'
)

export const ShowcaseMatcher = TagMatcher(
  'Showcase',
  ({id, ids, fontsize, carousel, ...attr}) => {
    return <Showcase otid={id} otids={ids} fontSize={fontsize} carousel={carousel} {...attr} />
  },
  {linkid: '', id: '', gap: '24', fontsize: '18px', caption: ''},
  'Showcase by id'
)

export const CustomCSSMatcher = TagMatcher(
  'CustomCSS',
  ({text, childEls}) => {
    let apiurl = useSelector(Sel.apiurl)

    let cssCode = childEls
      .filter(e => e.tag == 'br')
      .map(e => e.tail)
      .join('')

    let fontFace = childEls
      .filter(e => e.tag == 'FontFace')
      .map(e => {
        e = e.attrib
        let src = apiurl.href(`/domainresource/static/css/font/${e.src}`)
        return `@font-face{
          font-family: ${e.fontfamily};
          src: url('${src}');
          font-weight: ${e.fontweight};
          font-style: ${e.fontstyle};
        }`
      })

    return (
      <Style type="text/css" id="presentation">
        {cssCode}
        {text}
        {fontFace.map(e => e)}
      </Style>
    )
  },
  {},
  'Provides html-head with some additional <style/>'
)

export const CustomFontFaceMatcher = TagMatcher(
  'FontFace',
  ({applyTemplates}) => {
    return applyTemplates()
  },
  {fontfamily: '', src: 'url.woff2', fontweight: '', fontstyle: ''},
  'add FontFace as child of CustomCSS to make it available in dev & build'
)

const OutgoingExhibitionLink = type =>
  TagMatcher(
    `Ex${type}`,
    ({id, title, linkIcon, linkicon, applyTemplates}) => (
      <RawLink linkIcon={linkIcon || linkicon} href={`/${id}`} target="_external" title={title}>
        {applyTemplates()}
      </RawLink>
    ),
    {id: '', linkIcon: false},
    `Outgoing ${type} link`
  )

export const ExTitleMatcher = OutgoingExhibitionLink('Title')
export const ExPageMatcher = OutgoingExhibitionLink('Page')
export const ExPersonMatcher = OutgoingExhibitionLink('Person')
export const ExPlaceMatcher = OutgoingExhibitionLink('Place')
export const ExSubjectMatcher = OutgoingExhibitionLink('Subject')

export const CaptionMatcher = TagMatcher(
  'Description',
  ({applyTemplates, linkid, caption}) => {
    let {mobile} = useWikiContext()
    let content = caption ? useCaptionLink(caption, linkid) : applyTemplates()
    return (
      <div className={clsx(S.caption, mobile && S.mobile)}>
        <div className={GlobalS.caption}>{content}</div>
      </div>
    )
  },
  {linkid: '', caption: ''},
  'Creates and styles (caption) description element'
)

export const ImageViewerMatcher = TagMatcher(
  'ImageViewer',
  ({applyTemplates, childEls, ...props}) => {
    let data_opts = {}
    let {mobile} = useWikiContext()
    let {id, ids, ...more_opts} = props

    if (id) {
      data_opts = {...data_opts, otid: parseInt(id)}
    }

    if (ids) {
      let otids = String(ids)
        .split(',')
        .map(otid => parseInt(otid))
        .filter(Boolean)
      data_opts = {...data_opts, otids}
    }
    let {id: otid, ids: otids, ...opts} = more_opts
    let p = {...data_opts, ...opts}
    return <PageViewer {...p} mobile={mobile} />
  },
  'Page viewer for a given root id'
)

export const TransitionMatcher = TagMatcher(
  'Transition',
  ({applyTemplates, ...attrs}) => {
    let {mobile} = useWikiContext()
    let {duration, delay, triggerOnce, type} = attrs

    // XXX [dr]: triggerOnce == false produces cpu burning endless loops
    triggerOnce = true
    let [ref, onScreen] = useInView({triggerOnce})
    return (
      <div
        ref={ref}
        style={{animationDelay: delay, animationDuration: duration}}
        className={clsx(onScreen && S.show, !mobile && S.animation, onScreen && S[type])}
      >
        {applyTemplates()}
      </div>
    )
  },
  'Transition on children'
)

export const ModalContentMatcher = TagMatcher(
  'ModalContent',
  ({applyTemplates}) => applyTemplates(),
  () => ({}) // tag completion only via ModalMatcher
)

export const IFrameMatcher = TagMatcher('iframe', ({width, height, src}) => {
  let [allowed, setAllowed] = useState(false)

  if (!allowed) {
    return (
      <div
        className={S.iframeDisclaimer}
        style={{
          width: width,
          height: height,
        }}
      >
        <p>
          An dieser Stelle werden externe Resourcen geladen. Bitte erlauben Sie dies, um
          fortzufahren.
        </p>
        <Button inverted className={S.button} circle={false} onClick={() => setAllowed(true)}>
          Externen Inhalt laden
        </Button>
      </div>
    )
  }

  return <iframe src={src} height={height} width={width}></iframe>
})

export const ModalMatcher = TagMatcher(
  'Modal',
  ({
    applyTemplates,
    backgroundColor,
    backgroundOpacity,
    contentColor,
    text,
    caption,
    hideCaption,
    padding,
    linkid,
    childEls,
    inline = true,
    bgid,
    linkIcon,
    scrolling,
  }) => {
    let [modalOpen, setModalOpen] = useState(false)
    let {mobile} = useWikiContext()

    let contentEls = childEls.filter(e => e.tag != 'ModalContent')
    let modalContent = childEls.filter(e => e.tag == 'ModalContent').at(0)
    let modalCaption = useCaptionLink(caption, linkid, null, null, true)
    let percentToDecimal = percent => parseFloat(percent) / 100

    let modalStyles = {
      '--presentation_modal_color': contentColor || 'var(--presentation_meta_contentColor)',
      ...(padding && {'--presentation_modal_padding': padding}),
      '--presentation_modal_bgColor': backgroundColor || 'var(--presentation_meta_contentBgColor)',
      ...(backgroundOpacity && {
        '--presentation_modal_bgOpacity': percentToDecimal(backgroundOpacity),
      }),
    }

    return (
      <>
        {modalOpen && (
          <Modal
            mobile={mobile}
            caption={hideCaption ? '' : modalCaption}
            open={modalOpen}
            scrolling={scrolling}
            style={modalStyles}
            imageId={bgid}
            onClose={() => setModalOpen(false)}
          >
            {modalContent ? applyTemplates(modalContent) : <Message>no ModalContent</Message>}
          </Modal>
        )}
        <div
          style={{cursor: 'pointer', display: inline ? 'contents' : 'block'}}
          onClick={() => setModalOpen(true)}
        >
          <a>
            {[text, applyTemplates(contentEls)]}{' '}
            {linkIcon && <VIcon style={{fontSize: '.75em'}} name="link" />}
          </a>
        </div>
      </>
    )
  },
  () => ({
    '!top': ['Modal'],
    Modal: {
      attrs: {
        inline: BOOL_STR,
        bgid: '',
        scrolling: true,
        hideCaption: false,
        caption: '',
        linkIcon: false,
        type: ['zoom', 'viewer', 'text'],
      },
      children: ['ModalContent'],
    },
    ModalContent: {
      attrs: {},
    },
  })
)

export const TextBlock = TagMatcher(
  'TextBlock',
  ({
    applyTemplates,
    borderRadius,
    top,
    left,
    bottom,
    right,
    width,
    textAlign,
    fontSize,
    contentcolor,
    padding,
    backgroundColor,
    mobileAbsolute,
    imageSource,
  }) => {
    let {mobile} = useWikiContext()
    let addStyles = {}
    let apiurl = useSelector(Sel.apiurl)
    let id = isNaN(imageSource) ? null : imageSource
    let imgsrc = id && useImageInfo({id}).imgsrc
    let backgroundImage = null
    if (imgsrc || id) {
      backgroundImage = imgsrc ? `url(${imgsrc})` : `url(${apiurl.href(imageSource)})`
    }

    let normalizeMobilePos = value => {
      if (value) {
        return value.startsWith('-') ? '-1rem' : value
      }
    }
    if (mobile) {
      if (!mobileAbsolute) {
        addStyles = {
          top: 'inherit',
          bottom: 'inherit',
          left: 'inherit',
          right: 'inherit',
          position: 'relative',
          width: borderRadius === '100%' ? width : '100%',
        }
      } else {
        addStyles = {
          top: normalizeMobilePos(top),
          bottom: normalizeMobilePos(bottom),
          right: normalizeMobilePos(right),
          left: normalizeMobilePos(left),
        }
      }
    }

    let blockStyle = {
      top,
      left,
      bottom,
      right,
      width,
      backgroundColor: backgroundImage ? 'transparent' : backgroundColor,
      padding,
      borderRadius,
      textAlign,
      fontSize,
      backgroundImage,
      '--widget_textblock_color': contentcolor,
      ...addStyles,
    }
    return (
      <div style={blockStyle} className={clsx(S.textblock, 'presTextblock')}>
        {applyTemplates()}
      </div>
    )
  },
  {
    top: 'inherit',
    left: 'inherit',
    bottom: 'inherit',
    right: 'inherit',
    width: 'auto',
    textalign: 'left',
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
    fontsize: '0.85em',
    contentcolor: 'inherit',
    padding: '1em',
    borderRadius: '0',
    mobileAbsolute: false,
  }
)

export const AudioPlayer = TagMatcher(
  'AudioPlayer',
  props => {
    return <MediaPlayer.Audio {...props} />
  },
  {
    url: 'url',
    src: 'src',
    caption: '',
    id: 0,
    type: '',
    preload: 'none',
  }
)

export const VideoPlayer = TagMatcher(
  'VideoPlayer',
  props => {
    return <MediaPlayer.Video {...props} />
  },
  {
    src: null,
    poster: null,
    caption: '',
    id: 0,
    type: '',
    embedded: false,
    preload: 'none',
  }
)

export const TagCloudMatcher = TagMatcher(
  'TagCloud',
  ({type}) => <TagCloud className={S.tagcloud} toLegacy type={type} />,
  {
    type: ['name', 'keyword', 'place'],
  }
)

export const ExternalLinkMatcher = TagMatcher(
  'ExternalLink',
  ({href, applyTemplates, linkIcon, linkicon}) => (
    <RawLink linkIcon={linkIcon || linkicon} href={href} target="blank">
      {applyTemplates()}
    </RawLink>
  ),
  {
    href: '',
    linkIcon: false,
  }
)

export const SearchTermMatcher = TagMatcher(
  'SearchTerm',
  ({text, searchterm}) => {
    let apiurl = useSelector(Sel.apiurl)
    let href = apiurl.href(`/search/quick?query=${searchterm}`)
    return (
      <Button className={S.searchTag} circle={false}>
        <RawLink target="_external" href={href}>
          Mehr zu {text ? text : searchterm}
        </RawLink>
      </Button>
    )
  },
  {searchterm: ''}
)

const SvgAnnotationContext = createContext({triggerFuncs: {}})

export const matchSvgAnnotation = {
  match: ({tag, parent, attrib}) => {
    if (attrib.id?.startsWith('vl-') && tag !== 'AnnotationItem') {
      while (parent) {
        if (parent.tag == 'svg') return true
        // parents of parents are WeakRef objects
        parent = parent instanceof WeakRef ? parent.deref() : parent.parent
      }
    }
    return false
  },
  build: ({tag, attrib, applyTemplates}) => (
    <SvgAnnotationContext.Consumer>
      {ctx =>
        React.createElement(tag, {
          ...attrib,
          className: clsx(
            attrib.className,
            {
              selected:
                ctx.selectedTarget === attrib.id ||
                (ctx.availableTargets?.length > 0 &&
                  ctx.availableTargets.every(target => target === attrib.id)),
            },

            {active: ctx.availableTargets?.includes(attrib.id)}
          ),
          onClick: () => ctx.triggerFuncs(attrib.id),
          children: applyTemplates(),
          style: {cursor: 'pointer'},
        })
      }
    </SvgAnnotationContext.Consumer>
  ),
}

const ImageWebResource = ({canvasid, applyTemplates, buildNodeSet}) => {
  /* a SVG-Resource, that is given by a canvasid (probably a webresource 9/2)
  // XXX: maybe support raster images as well
  */
  let [ref, inView] = useInView({triggerOnce: true})
  let {data, error} = useApi('presentation/getSvgAsset', {otid: canvasid}, {condition: inView})
  let res = null
  if (data) {
    if (data.svg) {
      let svg_nodes = buildNodeSet(data.svg)
      res = applyTemplates(svg_nodes)
    } else {
      // raster image resource?
      res = 'bad svg data'
    }
  }

  return <div ref={ref}>{res}</div>
}

export const AnnotationCanvasMatcher = TagMatcher(
  'AnnotationCanvas',
  ({util, applyTemplates, childEls, seqmode, canvasid, top, left, width}) => {
    let {mobile} = useWikiContext()
    // selected by user
    let [selectedTarget, setSelectedTarget] = useState(null)
    let [refid, setRefid] = useState(null)

    // active point on timeline
    let [availableTargets, setAvailableTargets] = useState(null)

    // get all Annotation children that have target attrib
    let annotationItems = childEls.filter(e => e.tag == 'AnnotationItem' && e.attrib.target)

    //minify annotationItems for less calculation later on
    let annotations = annotationItems.map(e => ({
      seqidx: e.attrib.seqidx,
      target: e.attrib.target,
      refid: e.attrib.resolved_refid,
    }))

    let annotationsByTarget = groupBy(annotations, e => e.target)
    //Timeline:
    let annotationsBySeqIdx = groupBy(annotations, a => a.seqidx)
    let seqidxs = keys(annotationsBySeqIdx)
    let first_seqidx = seqidxs.at(0)
    let last_seqidx = seqidxs.at(-1)
    let emptyCount = 0
    let even = false

    const renderTimelineItem = seqidx => {
      if (seqidx > last_seqidx) return null // limits rendering
      let activeSeq = annotationsBySeqIdx[seqidx] ?? []
      let selectedSeq = refid ? activeSeq.find(e => refid === e.refid) : false
      let hasContent = activeSeq.length > 0
      let nextHasContent = annotationsBySeqIdx[seqidx + 1]?.length > 0

      // activate timelineItem based on:
      // -selected seqidx has only one AnnotationItem,
      // -selected AnnotationItem including this seqidx,
      // or availableTargets including selected seqidx,
      let activeState = refid
        ? selectedSeq?.refid === refid
        : activeSeq.some(e => selectedTarget === e.target || availableTargets?.includes(e.target))

      //!hasContent: renders empty elements between two sequences
      //even: makes the timelineItem changing vertical position on the timeline
      if (!hasContent && !nextHasContent) {
        emptyCount = emptyCount + 1
      } else {
        emptyCount = hasContent ? 1 : emptyCount + 1
        if (hasContent) {
          even = !even
        }
        return (
          <div
            key={seqidx}
            style={{flex: emptyCount}}
            className={clsx(
              S.navItemContainer,
              mobile ? S.mobile : '',
              hasContent && S.filled,
              activeState && S.active,
              !even && S.odd
            )}
            onClick={() => {
              if (hasContent) {
                setAvailableTargets(activeSeq && activeSeq.map(a => a.target))
                // set selected values when all targets same or one just one selected
                let sameTarget = activeSeq.every(a => a.target === activeSeq[0].target)
                setSelectedTarget(sameTarget ? activeSeq[0].target : undefined)
                setRefid(sameTarget ? activeSeq[0].refid : null)
              }
            }}
          >
            <div className={S.navItem}>
              {hasContent && (
                <>
                  <span>{seqidx}</span>
                  <div className={S.circle}></div>
                </>
              )}
            </div>
          </div>
        )
      }
    }

    let navigationTimeline = (
      <div className={S.timeline}>
        {range(first_seqidx, last_seqidx + 1).map(renderTimelineItem)}
      </div>
    )

    let ctx = {
      triggerFuncs: e => (setSelectedTarget(e), setAvailableTargets([]), setRefid(null)),
      selectedTarget: selectedTarget,
      availableTargets: availableTargets,
    }
    let canvasElement = null
    // note: childEls hold only one child, which is the root Element, i.e. <AnnotationCanvas>
    // for now assume that <svg> element is top level child of AnnotationCanvas
    let svg_or_img = childEls.filter(e => e.tag == 'svg' || e.tag == 'img')
    if (svg_or_img.length > 0) {
      canvasElement = applyTemplates(svg_or_img)
    } else if (canvasid) {
      canvasElement = (
        <ImageWebResource
          canvasid={canvasid}
          applyTemplates={applyTemplates}
          buildNodeSet={util.buildNodeSet}
        />
      )
    }

    let currentIndex = annotations.findIndex(e => {
      if (refid) {
        return e.refid == refid
      } else if (selectedTarget) {
        // checks if there is just one target or identical targets (target, seqidx)
        let targets = annotationsByTarget[selectedTarget] ?? []
        let identicalTargets = targets.every(
          t => t.target === targets[0].target && t.seqidx === targets[0].seqidx
        )
        return e.target === selectedTarget && (targets.length === 1 || identicalTargets)
      }
    })

    let selectedAnnotation = annotationItems[currentIndex]
    let attrib = selectedAnnotation?.attrib || {}

    let annotationStyles = !mobile
      ? {
          position: 'absolute',
          top: attrib.top || top,
          left: attrib.left || left,
          width: attrib.width || width,
        }
      : null

    let goPrev = () => {
      if (currentIndex > 0) {
        let prevKey = annotations[currentIndex - 1]
        setAvailableTargets(null)
        setSelectedTarget(prevKey.target)
        setRefid(prevKey.refid)
      }
    }

    let goNext = () => {
      if (currentIndex < annotations.length - 1) {
        let nextKey = annotations[currentIndex + 1]
        setSelectedTarget(nextKey.target)
        setAvailableTargets(null)
        setRefid(nextKey.refid)
      }
    }

    let seqNavButtons = (
      <>
        {currentIndex > 0 && (
          <Button
            className={clsx(S.carouselButton, S.prev, mobile && S.mobile)}
            onClick={goPrev}
            icon="chevron left"
          />
        )}
        {currentIndex < annotations.length - 1 && (
          <Button
            className={clsx(S.carouselButton, S.next, mobile && S.mobile)}
            onClick={goNext}
            icon="chevron right"
          />
        )}
      </>
    )

    return (
      <>
        <SvgAnnotationContext.Provider value={ctx}>
          <div className={clsx(S.svgContainer, mobile && S.mobile, seqmode && S.seqMode)}>
            {(seqmode || mobile) && seqNavButtons}
            {canvasElement}
          </div>
        </SvgAnnotationContext.Provider>
        {seqmode ? navigationTimeline : null}
        {selectedAnnotation && (
          <div className={clsx(S.annotation, mobile && S.mobile)} style={annotationStyles}>
            <div className={S.header}>
              <h2>{attrib.caption}</h2>
              {!mobile && (
                <Button
                  icon={'close'}
                  className={S.closeButton}
                  onClick={() => (
                    setRefid(null), setSelectedTarget(null), setAvailableTargets(null)
                  )}
                  size="tiny"
                  inverted
                  style={{
                    color: 'white',
                    border: '1px solid white',
                  }}
                />
              )}
            </div>
            <div className={S.content}>
              {selectedAnnotation.text}
              {applyTemplates(selectedAnnotation.children)}
            </div>
          </div>
        )}
      </>
    )
  },
  {
    caption: '',
    seqmode: false,
    top: '10%',
    left: '60%',
    width: '40%',
  }
)
